import DateAndValue from "../DateAndValue";

export default class HDEAnalysis {
  constructor({
    independence = "AUTO",
    percentage = false,
    yearsFormatted = [],
    autoAssumptions = {},
    manualSalesRevenue = yearsFormatted.map((year) => ({ date: year, value: 0 })),
    manualNetIncome = yearsFormatted.map((year) => ({ date: year, value: 0 })),
    manualInterest = yearsFormatted.map((year) => ({ date: year, value: 0 })),
    manualTaxes = yearsFormatted.map((year) => ({ date: year, value: 0 })),
    manualDepreciation = yearsFormatted.map((year) => ({ date: year, value: 0 })),
    manualAmortization = yearsFormatted.map((year) => ({ date: year, value: 0 })),
    socialSecurityTax = 0,
    medicareTax = 0,
    matrixOwnershipSalary = {
      matrix: [
        {
          id: 0,
          name: "",
          series: yearsFormatted.map((year) => ({ date: year, value: "" })),
          comment: "",
        },
      ],
      note: "",
    },
    matrixOwnerRetirementContributions = {
      matrix: [
        {
          id: 0,
          name: "",
          series: yearsFormatted.map((year) => ({ date: year, value: "" })),
          comment: "",
        },
      ],
      note: "",
    },
    matrixOwnerHealthInsuranceBenefits = {
      matrix: [
        {
          id: 0,
          name: "",
          series: yearsFormatted.map((year) => ({ date: year, value: "" })),
          comment: "",
        },
      ],
      note: "",
    },
    matrixNonOperatingExpenses = {
      matrix: [
        {
          id: 0,
          name: "",
          series: yearsFormatted.map((year) => ({ date: year, value: "" })),
          comment: "",
        },
      ],
      note: "",
    },
    matrixNonRecurringExpenses = {
      matrix: [
        {
          id: 0,
          name: "",
          series: yearsFormatted.map((year) => ({ date: year, value: "" })),
          comment: "",
        },
      ],
      note: "",
    },
    matrixOverUnderPaymentAdjustments = {
      matrix: [
        {
          id: 0,
          name: "",
          series: yearsFormatted.map((year) => ({ date: year, value: "" })),
          comment: "",
        },
      ],
      note: "",
    },
  }) {
    this.independence = independence;
    this.percentage = percentage;
    this.yearsFormatted = yearsFormatted;
    this.autoAssumptions = autoAssumptions;
    this.manualSalesRevenue = manualSalesRevenue;
    this.manualNetIncome = manualNetIncome;
    this.manualInterest = manualInterest;
    this.manualTaxes = manualTaxes;
    this.manualDepreciation = manualDepreciation;
    this.manualAmortization = manualAmortization;
    this.socialSecurityTax = socialSecurityTax;
    this.medicareTax = medicareTax;
    this.matrixOwnershipSalary = matrixOwnershipSalary;
    this.matrixOwnerRetirementContributions = matrixOwnerRetirementContributions;
    this.matrixOwnerHealthInsuranceBenefits = matrixOwnerHealthInsuranceBenefits;
    this.matrixNonOperatingExpenses = matrixNonOperatingExpenses;
    this.matrixNonRecurringExpenses = matrixNonRecurringExpenses;
    this.matrixOverUnderPaymentAdjustments = matrixOverUnderPaymentAdjustments;
  }

  get manualSalesRevenueMargin() {
    const { manualSalesRevenue, totalSalesRevenue } = this;
    return this.getMargin(manualSalesRevenue, totalSalesRevenue);
  }

  get manualNetIncomeMargin() {
    const { manualNetIncome, totalSalesRevenue } = this;
    return this.getMargin(manualNetIncome, totalSalesRevenue);
  }

  get manualInterestMargin() {
    const { manualInterest, totalSalesRevenue } = this;
    return this.getMargin(manualInterest, totalSalesRevenue);
  }

  get manualTaxesMargin() {
    const { manualTaxes, totalSalesRevenue } = this;
    return this.getMargin(manualTaxes, totalSalesRevenue);
  }

  get manualDepreciationMargin() {
    const { manualDepreciation, totalSalesRevenue } = this;
    return this.getMargin(manualDepreciation, totalSalesRevenue);
  }

  get manualAmortizationMargin() {
    const { manualAmortization, totalSalesRevenue } = this;
    return this.getMargin(manualAmortization, totalSalesRevenue);
  }

  get totalEBITDAasDefined() {
    const { manualNetIncome, manualInterest, manualTaxes, manualDepreciation, manualAmortization } = this;

    if(this.independence === "AUTO") return this.autoAssumptions.EBITDA;
    else {
      return this.getArraySum(manualNetIncome, this.getArraySum(
        manualInterest, this.getArraySum(
          manualTaxes, this.getArraySum(manualDepreciation, manualAmortization)
        )
      ));
    }
  }

  get totalEBITDAasDefinedMargin() {
    const { totalEBITDAasDefined, totalSalesRevenue } = this;
    return this.getMargin(totalEBITDAasDefined, totalSalesRevenue);
  }

  get totalSalesRevenue() {
    if(this.independence === "AUTO") return this.autoAssumptions.salesRevenue;
    else return this.manualSalesRevenue;
  }

  get totalSalesRevenueMargin() {
    const { totalSalesRevenue } = this;
    return this.getMargin(totalSalesRevenue, totalSalesRevenue);
  }

  get totalOwnershipSalary() {
    const { matrixOwnershipSalary } = this;
    return this.getMatrixSum(matrixOwnershipSalary);
  }

  get totalOwnershipSalaryMargin() {
    const { totalOwnershipSalary, totalSalesRevenue } = this;
    return this.getMargin(totalOwnershipSalary, totalSalesRevenue);
  }

  get totalSocialSecurityTaxes() {
    const { totalOwnershipSalary, socialSecurityTax } = this;
    return this.getArrayMultiply(totalOwnershipSalary, socialSecurityTax);
  }

  get totalSocialSecurityTaxesMargin() {
    const { totalSocialSecurityTaxes, totalSalesRevenue } = this;
    return this.getMargin(totalSocialSecurityTaxes, totalSalesRevenue);
  }

  get totalMedicareTaxes() {
    const { totalOwnershipSalary, medicareTax } = this;
    return this.getArrayMultiply(totalOwnershipSalary, medicareTax);
  }

  get totalMedicareTaxesMargin() {
    const { totalMedicareTaxes, totalSalesRevenue } = this;
    return this.getMargin(totalMedicareTaxes, totalSalesRevenue);
  }

  get totalOwnershipSalaryandPayrollTaxes() {
    const { totalOwnershipSalary, socialSecurityTax, medicareTax } = this;
    return this.getArrayMultiply(totalOwnershipSalary, 1 + socialSecurityTax + medicareTax);
  }

  get totalOwnershipSalaryandPayrollTaxesMargin() {
    const { totalOwnershipSalaryandPayrollTaxes, totalSalesRevenue } = this;
    return this.getMargin(totalOwnershipSalaryandPayrollTaxes, totalSalesRevenue);
  }

  get totalOwnerRetirementContributions() {
    const { matrixOwnerRetirementContributions } = this;
    return this.getMatrixSum(matrixOwnerRetirementContributions);
  }

  get totalOwnerRetirementContributionsMargin() {
    const { totalOwnerRetirementContributions, totalSalesRevenue } = this;
    return this.getMargin(totalOwnerRetirementContributions, totalSalesRevenue);
  }

  get totalOwnerHealthInsuranceBenefits() {
    const { matrixOwnerHealthInsuranceBenefits } = this;
    return this.getMatrixSum(matrixOwnerHealthInsuranceBenefits);
  }

  get totalOwnerHealthInsuranceBenefitsMargin() {
    const { totalOwnerHealthInsuranceBenefits, totalSalesRevenue } = this;
    return this.getMargin(totalOwnerHealthInsuranceBenefits, totalSalesRevenue);
  }

  get totalNonOperatingExpenses() {
    const { matrixNonOperatingExpenses } = this;
    return this.getMatrixSum(matrixNonOperatingExpenses);
  }

  get totalNonOperatingExpensesMargin() {
    const { totalNonOperatingExpenses, totalSalesRevenue } = this;
    return this.getMargin(totalNonOperatingExpenses, totalSalesRevenue);
  }

  get totalNonRecurringExpenses() {
    const { matrixNonRecurringExpenses } = this;
    return this.getMatrixSum(matrixNonRecurringExpenses);
  }

  get totalNonRecurringExpensesMargin() {
    const { totalNonRecurringExpenses, totalSalesRevenue } = this;
    return this.getMargin(totalNonRecurringExpenses, totalSalesRevenue);
  }

  get totalOverUnderPaymentAdjustments() {
    const { matrixOverUnderPaymentAdjustments } = this;
    return this.getMatrixSum(matrixOverUnderPaymentAdjustments);
  }

  get totalOverUnderPaymentAdjustmentsMargin() {
    const { totalOverUnderPaymentAdjustments, totalSalesRevenue } = this;
    return this.getMargin(totalOverUnderPaymentAdjustments, totalSalesRevenue);
  }

  get totalDiscretionaryOwnerEarnings() {
    const {
      totalEBITDAasDefined,
      totalOwnershipSalaryandPayrollTaxes,
      totalOwnerRetirementContributions,
      totalOwnerHealthInsuranceBenefits,
      totalNonOperatingExpenses,
      totalNonRecurringExpenses,
      totalOverUnderPaymentAdjustments,
    } = this;
    return this.getMultipleSum(
      totalEBITDAasDefined,
      totalOwnershipSalaryandPayrollTaxes,
      totalOwnerRetirementContributions,
      totalOwnerHealthInsuranceBenefits,
      totalNonOperatingExpenses,
      totalNonRecurringExpenses,
      totalOverUnderPaymentAdjustments,
    );
  }
  
  get totalDiscretionaryOwnerEarningsMargin() {
    const { totalDiscretionaryOwnerEarnings, totalSalesRevenue } = this;
    return this.getMargin(totalDiscretionaryOwnerEarnings, totalSalesRevenue);
  }

  getMatrixSum(matrix) {
    //add each year's sum of item values(prices)
    return this.yearsFormatted.map(
      (year, index) =>
        new DateAndValue({
          date: year,
          value: matrix.matrix.reduce(
            (acc, item) => (acc += Number(item.series[index].value)),
            0
          ),
        })
    );
  }

  getArraySum(array1, array2) {
    //add element values of arrays
    return this.yearsFormatted.map(
      (year, index) =>
        new DateAndValue({
          date: year,
          value: array1[index].value + array2[index].value,
        })
    );
  }

  getArrayMultiply(array, num) {
    //multiply number to element values of arrays
    return this.yearsFormatted.map(
      (year, index) =>
        new DateAndValue({
          date: year,
          value: array[index].value * num,
        })
    );
  }

  getMargin(array1, array2) {
    //get margin array from an array
    return this.yearsFormatted.map(
      (year, index) =>
        new DateAndValue({
          date: year,
          value: Number(array1[index].value) / array2[index].value,
        })
    );
  }

  getMultipleSum(arr1, arr2, arr3, arr4, arr5, arr6, arr7) {
    //get a sum array of 7 array elements

    return this.yearsFormatted.map(
      (year, index) =>
        new DateAndValue({
          date: year,
          value: 
            Number(arr1[index].value) +
            Number(arr2[index].value) +
            Number(arr3[index].value) +
            Number(arr4[index].value) +
            Number(arr5[index].value) +
            Number(arr6[index].value) +
            Number(arr7[index].value),
        })
    );
  }
}
